import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import MailIcon from '@mui/icons-material/Mail';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import { Box, Pagination } from '@mui/material';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import CollapsableColumns from 'components/ui/CollapsableColumns';
import SectionTitle from 'components/ui/typography/SectionTitle';
import dayjs from 'dayjs';
import TenantPage from 'layouts/TenantPage';
import { Fragment, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { notificationService } from 'shared/services';
import { loadingActions } from 'store';
import { useAppDispatch } from 'store/store';
import { INotification } from '../types';

/**
 * An enterprise multi-column notifications workspace.
 * Manages asynchronous unread status persistence loops on explicit click selection triggers.
 */
export const NotificationList = () => {
  const { t } = useTranslation();
  const prefix = 'pages.notifications';
  const dispatch = useAppDispatch();

  const [selectedNotification, setSelectedNotification] = useState<INotification>(null);
  const [notifications, setNotifications] = useState<INotification[]>([]);

  const [limit] = useState(10);
  const [offset, setOffset] = useState(0);
  const [count, setCount] = useState(0);

  useEffect(() => {
    dispatch(loadingActions.startLoading());
    notificationService
      .getAllPaginated(limit, offset)
      .then((res) => {
        setNotifications(res.results);
        setCount(res.count);
      })
      .catch((err) => {
        console.error('Failed to load notifications registry:', err);
      })
      .finally(() => {
        dispatch(loadingActions.stopLoading());
      });
  }, [limit, offset, dispatch]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setOffset((newPage - 1) * limit);
  };

  const onSelectNotification = (value: INotification) => {
    setSelectedNotification(value);

    if (!value.read) {
      notificationService
        .update(value.id, { read: true })
        .then(() => {
          setNotifications((prev) =>
            prev.map((item) => (item.id === value.id ? { ...item, read: true } : item))
          );
        })
        .catch((err) => console.error('Failed to persist read status flags:', err));
    }
  };

  return (
    <TenantPage title={t(`${prefix}.title`)} subtitle={t(`${prefix}.subtitle`)}>
      <CollapsableColumns
        size={selectedNotification ? 'w-1/2' : 'w-full'}
        expand={!!selectedNotification}
        onClose={() => setSelectedNotification(null)}
        contentLeft={
          <Box
            sx={{
              width: '100%',
              bgcolor: 'background.paper',
              borderRadius: 2,
              overflow: 'hidden'
            }}>
            <List disablePadding>
              {notifications.map((n) => {
                const isSelected = selectedNotification?.id === n.id;
                return (
                  <Fragment key={n.id}>
                    <ListItem
                      alignItems="flex-start"
                      onClick={() => onSelectNotification(n)}
                      sx={{
                        cursor: 'pointer',
                        bgcolor: isSelected ? 'action.selected' : 'transparent',
                        transition: 'background-color 0.2s',
                        py: 2,
                        px: 3,
                        position: 'relative',
                        '&:hover': { bgcolor: 'action.hover' }
                      }}>
                      {/* Interactive Custom Pulsing Read Status Indicator Dot */}
                      {!n.read && (
                        <Box
                          sx={{
                            position: 'absolute',
                            right: 24,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            display: 'flex',
                            alignItems: 'center'
                          }}>
                          <Box
                            sx={{
                              width: 8,
                              height: 8,
                              bgcolor: 'primary.main',
                              borderRadius: '50%',
                              position: 'relative'
                            }}>
                            <Box
                              sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                bgcolor: 'primary.main',
                                borderRadius: '50%',
                                animation: 'pulse 1.5s infinite ease-in-out',
                                '@keyframes pulse': {
                                  '0%': { transform: 'scale(1)', opacity: 0.8 },
                                  '100%': { transform: 'scale(3)', opacity: 0 }
                                }
                              }}
                            />
                          </Box>
                        </Box>
                      )}

                      <ListItemText
                        primary={
                          <Typography
                            variant="body1"
                            sx={{ fontWeight: n.read ? 400 : 600, color: 'text.primary', pr: 4 }}>
                            {n.notification.message}
                          </Typography>
                        }
                        secondary={
                          <Box
                            display="flex"
                            alignItems="center"
                            gap={0.5}
                            sx={{ mt: 0.5, color: 'text.secondary' }}>
                            {n.read ? (
                              <MailOutlineIcon sx={{ fontSize: 14 }} />
                            ) : (
                              <MailIcon sx={{ fontSize: 14, color: 'primary.main' }} />
                            )}
                            <Typography variant="caption" sx={{ fontWeight: 500 }}>
                              {dayjs(n.notification.timestamp).format('HH:mm DD/MM/YYYY')}
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItem>
                    <Divider component="li" />
                  </Fragment>
                );
              })}
            </List>

            {/* Pagination Controls Wrapper */}
            {count > limit && (
              <Box display="flex" justifyContent="center" sx={{ mt: 3, pb: 2 }}>
                <Pagination
                  count={Math.ceil(count / limit)}
                  page={Math.ceil(offset / limit) + 1}
                  onChange={handleChangePage}
                  color="primary"
                />
              </Box>
            )}
          </Box>
        }
        contentRight={
          selectedNotification && (
            <Box
              sx={{
                p: 4,
                bgcolor: 'background.paper',
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'divider',
                boxShadow: '0 2px 12px rgba(0, 0, 0, 0.02)',
                height: 'fit-content'
              }}>
              <Box
                display="flex"
                alignItems="center"
                gap={1}
                sx={{ color: 'text.secondary', mb: 2 }}>
                <CalendarTodayIcon sx={{ fontSize: 15 }} />
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  {dayjs(selectedNotification.notification.timestamp).format('DD/MM/YYYY HH:mm')}
                </Typography>
              </Box>

              <SectionTitle>Dettaglio Messaggio</SectionTitle>

              <Divider sx={{ mb: 2.5 }} />

              <Typography
                variant="body1"
                sx={{ color: 'text.secondary', lineHeight: 1.7, wordBreak: 'break-word' }}>
                {selectedNotification.notification.message}
              </Typography>
            </Box>
          )
        }
      />
    </TenantPage>
  );
};
